import * as React from "react"
import { cn } from "../../lib/utils"
import { CalendarIcon, X } from "lucide-react"


export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {}

const Date = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, value, onChange, name, placeholder, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null)
    const [localValue, setLocalValue] = React.useState(value || '')

    React.useEffect(() => {
      setLocalValue(value || '')
    }, [value])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value)
      if (onChange) {
        onChange(e)
      }
    }

    const handleClear = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      
      setLocalValue('')
      if (onChange && name) {
        const event = {
          target: {
            name,
            value: ''
          }
        } as React.ChangeEvent<HTMLInputElement>
        onChange(event)
      }
    }

    const handleCalendarClick = () => {
      inputRef.current?.showPicker()
    }

    React.useEffect(() => {
      if (typeof ref === 'function') {
        ref(inputRef.current)
      } else if (ref) {
        ref.current = inputRef.current
      }
    }, [ref])

    return (
      <div className="relative">
        <input
          type="date"
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
            "file:border-0 file:bg-transparent file:text-sm file:font-medium",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-200 focus-visible:ring-offset-0",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "[&::-webkit-calendar-picker-indicator]:opacity-0",
            "pr-20",
            className
          )}
          ref={inputRef}
          value={localValue}
          onChange={handleChange}
          name={name}
          placeholder={placeholder}
          {...props}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {localValue && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full"
              type="button"
              title="Clear date"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          <button
            onClick={handleCalendarClick}
            className="p-1 hover:bg-gray-100 rounded-full"
            type="button"
            title="Open calendar"
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
      </div>
    )
  }
)
Date.displayName = "Date"

export { Date } 